<template>
	<div
		class="simulationCard"
		:class="{
			'is-open': isOpen,
			'simulationCard--dumb': dumb,
		}"
	>
		<div class="simulationCard__summary" @click="handleClick">
			<div class="simulationCard__name">{{ summary.name }}</div>

			<div class="simulationCard__statusAndControlsMobile">
				<div class="simulationCard__statusMobile">
					<StatusBadge :status="summary.calculationStatus" />
				</div>
				<div class="simulationCard__controlsMobile">
					<BBtn variant="text" shape="circle" @click.stop="handleEditClick">
						<BIcon name="Edit" size="20px" />
					</BBtn>

					<BBtn
						variant="text"
						shape="circle"
						@click.stop="handleDuplicateClick"
					>
						<BIcon name="Duplicate" size="20px" />
					</BBtn>

					<BBtn variant="text" shape="circle" @click.stop="handleDeleteClick">
						<BIcon name="Delete" size="20px" />
					</BBtn>
				</div>
			</div>

			<div class="simulationCard__details">
				<div class="simulationCard__detail simulationCard__type">
					{{ $t(type) }}
				</div>
				<div class="simulationCard__detail simulationCard__totalSpans">
					{{ summary.spans }} {{ $t("vãos") }}
				</div>
				<div class="simulationCard__statusDesktop">
					<StatusBadge :status="summary.calculationStatus" />
				</div>
				<div class="simulationCard__detail simulationCard__date">
					{{ updateDay }}
				</div>
				<div class="simulationCard__detail simulationCard__time">
					{{ updateTime }}
				</div>
			</div>

			<div class="simulationCard__controlsDesktop">
				<BBtn variant="text" shape="circle" @click.stop="handleEditClick">
					<BIcon name="Edit" size="20px" />
				</BBtn>

				<BBtn variant="text" shape="circle" @click.stop="handleDuplicateClick">
					<BIcon name="Duplicate" size="20px" />
				</BBtn>

				<BBtn variant="text" shape="circle" @click.stop="handleDeleteClick">
					<BIcon name="Delete" size="20px" />
				</BBtn>
			</div>
		</div>

		<div
			v-if="isOpen"
			class="simulationCard__body"
			:class="simulation ? 'is-withData' : 'is-withoutData'"
		>
			<div
				v-if="simulation"
				class="o-stack"
				:style="{
					gap: '40px',
				}"
			>
				<SummaryContentsGeneral
					sectionTitleStyle="inline"
					:values="simulation.values"
				/>
				<SummaryContentsLoads
					sectionTitleStyle="inline"
					:values="simulation.values"
				/>
				<SummaryContentsWind
					sectionTitleStyle="inline"
					:values="simulation.values"
				/>
				<SummaryContentsSnow
					sectionTitleStyle="inline"
					:values="simulation.values"
				/>
				<SummaryContentsOther
					sectionTitleStyle="inline"
					:values="simulation.values"
				/>
			</div>

			<div class="simulationCard__spinnerWrapper" v-if="isFetching">
				<BSpinner class="simulationCard__spinner" />
			</div>
		</div>
	</div>
</template>

<script>
import StatusBadge from "@/components/StatusBadge";
import BBtn from "@/components/base/BBtn";
import BIcon from "@/components/base/BIcon";
import BSpinner from "@/components/base/BSpinner";

import { mapGetters, mapState, mapActions } from "vuex";

import SummaryContentsGeneral from "@/components/SummaryContents-General.vue";
import SummaryContentsLoads from "@/components/SummaryContents-Loads.vue";
import SummaryContentsOther from "@/components/SummaryContents-Other.vue";
import SummaryContentsWind from "@/components/SummaryContents-Wind.vue";
import SummaryContentsSnow from "@/components/SummaryContents-Snow.vue";

export default {
	components: {
		StatusBadge,
		BBtn,
		BIcon,
		BSpinner,

		SummaryContentsGeneral,
		SummaryContentsLoads,
		SummaryContentsOther,
		SummaryContentsWind,
		SummaryContentsSnow,
	},

	props: {
		summary: {
			type: Object,
			required: true,
		},
		dumb: {
			type: Boolean,
			default: false,
		},
	},

	data() {
		return {
			isOpen: false,
			isFetching: false,
		};
	},

	computed: {
		...mapState("simulations", ["simulations"]),
		...mapGetters("simulations", ["getOption", "getSimulation"]),

		simulation() {
			return this.getSimulation(this.summary.id);
		},

		type() {
			return (
				this.getOption("type", this.summary.simulationType)?.label ??
				this.summary.simulationType
			);
		},

		updateDay() {
			const date = new Date(this.summary.lastUpdateDate);
			return `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`;
		},
		updateTime() {
			const date = new Date(this.summary.lastUpdateDate);
			return `${date.getHours()}h${date.getMinutes()}m`;
		},
	},

	methods: {
		...mapActions("simulations", [
			"fetchSimulation",
			"duplicateSimulation",
			"deleteSimulation",
		]),

		handleEditClick() {
			this.$router.push({
				name: "Simulator",
				params: {
					simulation: this.summary.id,
				},
			});
		},
		handleDuplicateClick() {
			this.duplicateSimulation({ id: this.summary.id })
				.then(({ id }) => {
					this.$router.push({
						name: "Simulator",
						params: {
							simulation: id,
						},
					});
				})
				.catch((err) => {});
		},
		handleDeleteClick() {
			this.deleteSimulation({ id: this.summary.id }).catch((err) => {});
		},

		handleClick() {
			if (this.dumb) return;

			this.isOpen = !this.isOpen;

			if (!this.isOpen) {
				return;
			}

			if (this.isFetching) {
				return;
			}

			this.isFetching = true;
			this.fetchSimulation(this.summary.id).finally(() => {
				this.isFetching = false;
			});
		},
	},
};
</script>

<style lang="scss">
.simulationCard {
	border-radius: 8px;
	background: var(--color-neutral-25, #fafafb);
	color: var(--color-neutral-600, #575b66);
	overflow: hidden;
	--padding-inline: 20px;
}

.simulationCard__summary {
	position: relative;
	display: grid;
	grid-template-columns: minmax(0, 1fr) minmax(0, 3fr) auto;
	align-items: center;
	padding: 30px var(--padding-inline);
	gap: 40px;

	.simulationCard:not(.simulationCard--dumb) & {
		cursor: pointer;
		&:hover {
			background: var(--color-neutral-0, #fff);
		}
	}
}

.simulationCard__name {
	color: var(--color-neutral-900, #575b66);
}

.simulationCard__details {
	display: grid;
	grid-template-columns: repeat(5, 1fr);
	gap: 40px;
}

.simulationCard__controlsDesktop {
	margin-left: auto;
	display: flex;
	gap: 15px;
	opacity: 0;
	visibility: hidden;
	transition: 0.12s;
	transform: translateX(10%);
	backdrop-filter: blur(4px);

	.simulationCard:not(.simulationCard--dumb):hover & {
		opacity: 1;
		visibility: visible;
		transform: translateX(0);
	}
}

.simulationCard__body {
	position: relative;
	min-height: 21rem;
	padding: 30px var(--padding-inline) 60px;
}

.simulationCard__spinnerWrapper {
	position: absolute;

	.simulationCard__body.is-withoutData & {
		top: 50%;
		left: 50%;
		transform: translate(-50%, -50%);
	}

	.simulationCard__body.is-withData & {
		transform: none;
		top: 1rem;
		right: 2rem;
	}
}

.simulationCard__statusAndControlsMobile {
	display: none;
	margin-left: auto;
}

.simulationCard__controlsMobile {
	display: none;
}

.simulationCard.is-open {
	.simulationCard__controlsMobile {
		display: block;
	}

	.simulationCard__statusMobile {
		position: absolute;
		right: var(--padding-inline);
		top: 5.5rem;
	}
}

@media (max-width: 1080px) {
	.simulationCard {
		.summary__grid {
			grid-template-columns: repeat(3, 1fr);
		}
	}
}

@media (max-width: 980px) {
	.simulationCard {
		.summary__grid {
			grid-template-columns: repeat(2, 1fr);
		}
	}
}

@media (max-width: 840px) {
	.simulationCard {
		.summary__section {
			grid-template-columns: repeat(2, 1fr);
		}
	}
}

@media (max-width: 768px) {
	.simulationCard__summary {
		grid-template-columns: 1fr;
		gap: 20px;
	}

	.simulationCard__details {
		grid-column: span 2;
		display: grid;
		grid-template-columns: repeat(2, minmax(0, 1fr));
		gap: 8px;
	}

	.simulationCard__detail {
		font-size: var(--font-size-s);
	}

	.simulationCard__statusAndControlsMobile {
		display: block;
	}

	.simulationCard__controlsDesktop,
	.simulationCard__statusDesktop {
		display: none;
	}

	.simulationCard {
		.summary__section {
			grid-template-columns: 1fr;
		}
	}
}
</style>
